import { useState } from "react";
import { api } from "../lib/api";
import { Search as SearchIcon, Loader2, Youtube, StickyNote, BookOpen, FileText } from "lucide-react";

const SOURCE_ICON = { video: Youtube, note: StickyNote, turbolearn: BookOpen, paper: FileText };
const SOURCE_LABEL = { video: "Lecture", note: "Note", turbolearn: "TurboLearn", paper: "Paper" };

export default function SearchPage() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/knowledge/search?q=${encodeURIComponent(q)}`);
      setResults(data.results);
      setConfidence(data.confidence);
    } catch { setResults([]); }
    finally { setBusy(false); }
  };

  const grouped = (results || []).reduce((acc, r) => { (acc[r.source_type] = acc[r.source_type] || []).push(r); return acc; }, {});

  return (
    <div className="h-screen overflow-y-auto p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-display text-3xl mb-1">Search</h1>
        <p className="text-sm text-[var(--aria-text-muted)] mb-6">Search across every connected source — lectures, notes, TurboLearn, and papers.</p>

        <form onSubmit={run} className="relative mb-8">
          <SearchIcon size={16} className="absolute left-4 top-4 text-[var(--aria-text-muted)]" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="e.g. protein folding entropy"
            data-testid="search-input"
            className="w-full pl-11 pr-4 py-3.5 rounded-md border border-[var(--aria-border)] bg-[var(--aria-surface)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm" />
          <button type="submit" disabled={busy} data-testid="search-submit"
            className="absolute right-2 top-2 btn btn-primary px-4 py-2 text-sm disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : "Search"}
          </button>
        </form>

        {confidence && (
          <div className="text-xs text-[var(--aria-text-muted)] mb-6 font-mono" data-testid="search-confidence">
            Confidence: {confidence.band} ({Math.round((confidence.score || 0))})
          </div>
        )}

        {results && results.length === 0 && (
          <div className="text-center py-16 text-[var(--aria-text-muted)]">No matches in your connected knowledge yet.</div>
        )}

        {Object.entries(grouped).map(([type, items]) => {
          const Icon = SOURCE_ICON[type] || FileText;
          return (
            <div key={type} className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <Icon size={15} className="text-[var(--aria-primary)]" />
                <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--aria-text-muted)]">{SOURCE_LABEL[type] || type}</h3>
              </div>
              <div className="space-y-3">
                {items.map((r, i) => (
                  <div key={i} className="aria-card p-4" data-testid={`search-result-${type}-${i}`}>
                    <div className="font-medium text-sm mb-1">{r.source_title}</div>
                    <div className="text-sm text-[var(--aria-text-muted)] leading-relaxed">{r.text?.slice(0, 280)}…</div>
                    {r.start_time != null && (
                      <div className="text-xs font-mono text-[var(--aria-primary)] mt-2">{Math.floor(r.start_time/60)}:{String(Math.floor(r.start_time%60)).padStart(2,"0")}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
